import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Divider,
  Autocomplete,
  Chip,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'

const CONDITION_TYPES = [
  {
    value: 'assessment_score',
    label: 'Assessment Score',
    operators: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
    fields: ['instrument_code', 'value'],
  },
  {
    value: 'assessment_trend',
    label: 'Assessment Trend (Last N)',
    operators: ['all_above', 'all_below', 'all_equal', 'increasing', 'decreasing'],
    fields: ['instrument_code', 'count', 'value'],
  },
]

const ACTION_TYPES = [
  { value: 'set_risk_factor', label: 'Set Risk Factor' },
  { value: 'clear_risk_factor', label: 'Clear Risk Factor' },
]

function ContextRuleEditorDialog({
  open,
  onClose,
  rule,
  onSave,
  saving,
  interventionRiskCodes = [],
  allRiskCodes = [],
  allAssessments = [],
}) {
  // Build lookup for assessment instruments by code.
  const assessmentMap = new Map(
    (allAssessments || []).map((a) => [a.code, a])
  )

  // Derive the numeric scoring range from an instrument's scoring_logic.
  // scoring_logic.max_score is reliably populated; min_score defaults to 0.
  const getAssessmentRange = (code) => {
    const inst = assessmentMap.get(code)
    if (!inst) return null
    const logic = inst.scoring_logic || {}
    const max = Number(logic.max_score)
    if (!Number.isFinite(max)) return null
    const min = Number.isFinite(Number(logic.min_score)) ? Number(logic.min_score) : 0
    return { min, max, name: inst.name }
  }

  const isValueOutOfRange = (instrumentCode, value) => {
    const range = getAssessmentRange(instrumentCode)
    if (!range) return false
    if (value === '' || value === null || value === undefined) return false
    const n = Number(value)
    if (!Number.isFinite(n)) return false
    return n < range.min || n > range.max
  }
  const isEditMode = !!rule

  const [formData, setFormData] = useState({
    priority_order: 10,
    rule_definition: {
      match_mode: 'all',
      conditions: [],
      actions: [],
    },
  })

  const [error, setError] = useState(null)

  useEffect(() => {
    if (open) {
      if (rule) {
        setFormData({
          priority_order: rule.priority_order ?? rule.rule_priority ?? 10,
          rule_definition: rule.rule_definition || {
            match_mode: 'all',
            conditions: [],
            actions: [],
          },
        })
      } else {
        setFormData({
          priority_order: 10,
          rule_definition: {
            match_mode: 'all',
            conditions: [],
            actions: [],
          },
        })
      }
      setError(null)
    }
  }, [open, rule])

  // Risk-code lookup maps (parallel to StrategyEditorDialog).
  const interventionCodeSet = new Set(
    (interventionRiskCodes || []).map(rc => rc.code).filter(Boolean)
  )
  const allRiskCodeMap = new Map(
    (allRiskCodes || []).map(rc => [rc.code, rc])
  )
  for (const rc of interventionRiskCodes || []) {
    if (rc.code) allRiskCodeMap.set(rc.code, rc)
  }

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const updateDefinition = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      rule_definition: { ...prev.rule_definition, [field]: value },
    }))
  }

  const addCondition = () => {
    updateDefinition('conditions', [
      ...formData.rule_definition.conditions,
      { type: 'assessment_score', operator: 'greater_than', instrument_code: '', value: '' },
    ])
  }

  const updateCondition = (index, field, value) => {
    const conditions = [...formData.rule_definition.conditions]
    conditions[index] = { ...conditions[index], [field]: value }
    updateDefinition('conditions', conditions)
  }

  const removeCondition = (index) => {
    updateDefinition(
      'conditions',
      formData.rule_definition.conditions.filter((_, i) => i !== index)
    )
  }

  const addAction = () => {
    updateDefinition('actions', [
      ...formData.rule_definition.actions,
      { type: 'set_risk_factor', risk_code: '', value: '' },
    ])
  }

  const updateAction = (index, patch) => {
    const actions = [...formData.rule_definition.actions]
    actions[index] = { ...actions[index], ...patch }
    updateDefinition('actions', actions)
  }

  const removeAction = (index) => {
    updateDefinition(
      'actions',
      formData.rule_definition.actions.filter((_, i) => i !== index)
    )
  }

  const handleSave = async () => {
    if (formData.rule_definition.conditions.length === 0) {
      setError('At least one condition is required')
      return
    }
    if (formData.rule_definition.actions.length === 0) {
      setError('At least one action is required')
      return
    }
    try {
      setError(null)
      await onSave(formData)
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save patient rule')
    }
  }

  const handleClose = () => {
    setError(null)
    onClose()
  }

  const getConditionType = (type) => CONDITION_TYPES.find((ct) => ct.value === type)

  // Build risk-code autocomplete options, including any currently-referenced
  // orphan code flagged as "not in intervention".
  const buildRiskCodeOptions = (selectedCode) => {
    const options = (interventionRiskCodes || []).map(rc => ({
      code: rc.code,
      label: rc.name || rc.code,
      risk: rc,
      orphan: false,
    }))
    if (selectedCode && !interventionCodeSet.has(selectedCode)) {
      const full = allRiskCodeMap.get(selectedCode)
      options.push({
        code: selectedCode,
        label: full?.name || selectedCode,
        risk: full || null,
        orphan: true,
      })
    }
    return options
  }

  // Render the value control for a set_risk_factor action based on the
  // selected risk code's format.
  const renderSetRiskValueControl = (action, index) => {
    const rc = allRiskCodeMap.get(action.risk_code)
    const format = rc?.value_format
    if (!rc) {
      // No code selected yet — show a disabled placeholder.
      return (
        <TextField size="small" label="Value" value="" disabled sx={{ minWidth: 140 }} />
      )
    }
    if (format === 'presence') {
      return null
    }
    if (format === 'discrete') {
      const allowed = Array.isArray(rc.discrete_values) ? rc.discrete_values : []
      return (
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Value</InputLabel>
          <Select
            value={action.value ?? ''}
            label="Value"
            onChange={(e) => updateAction(index, { value: e.target.value })}
          >
            {allowed.map(v => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    }
    if (format === 'numeric') {
      return (
        <TextField
          size="small"
          type="number"
          label={`Value (${rc.numeric_min}–${rc.numeric_max})`}
          value={action.value ?? ''}
          onChange={(e) => updateAction(index, { value: e.target.value })}
          inputProps={{ min: rc.numeric_min, max: rc.numeric_max }}
          sx={{ minWidth: 160 }}
        />
      )
    }
    return null
  }

  const renderRiskCodeAutocomplete = (action, index) => {
    const options = buildRiskCodeOptions(action.risk_code)
    const selected = options.find(o => o.code === action.risk_code) || null
    const isOrphan = !!selected?.orphan

    return (
      <Autocomplete
        size="small"
        options={options}
        value={selected}
        onChange={(_, newVal) => updateAction(index, {
          risk_code: newVal?.code || '',
          // Reset the value when the code changes — format may differ.
          value: '',
        })}
        getOptionLabel={(o) => o?.label || o?.code || ''}
        isOptionEqualToValue={(o, v) => o?.code === v?.code}
        renderOption={(props, option) => (
          <li {...props} key={option.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.code} · {option.risk?.value_format || '—'}
                </Typography>
              </Box>
              {option.orphan && (
                <Chip size="small" color="warning" label="not in intervention" sx={{ height: 18, fontSize: '0.625rem' }} />
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Risk Code"
            error={isOrphan}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isOrphan && (
                    <Tooltip title="This risk code is not attached to this intervention. Re-add it in the Risk Codes tab, or change this rule to use a different code.">
                      <WarningIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />
                    </Tooltip>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ minWidth: 200 }}
      />
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Patient Rule' : 'Create Patient Rule'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Conditions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
              Conditions (When to Apply)
            </Typography>
            <RadioGroup
              row
              value={formData.rule_definition.match_mode || 'all'}
              onChange={(e) => updateDefinition('match_mode', e.target.value)}
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="Match ALL" />
              <FormControlLabel value="any" control={<Radio size="small" />} label="Match ANY" />
            </RadioGroup>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
            Add Condition
          </Button>
        </Box>

        {formData.rule_definition.conditions.map((condition, index) => {
          const conditionType = getConditionType(condition.type)
          const range = getAssessmentRange(condition.instrument_code)
          const outOfRange = isValueOutOfRange(condition.instrument_code, condition.value)
          const assessmentOptions = (allAssessments || []).map((a) => ({
            code: a.code,
            label: `${a.code} — ${a.name || 'Unnamed'}`,
            instrument: a,
          }))
          const selectedAssessment = assessmentOptions.find((o) => o.code === condition.instrument_code) || null

          return (
            <Box key={index} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1, px: 1, py: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 36, fontWeight: 500, flexShrink: 0 }}>
                {index === 0 ? 'When' : formData.rule_definition.match_mode === 'all' ? 'AND' : 'OR'}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={condition.type}
                  displayEmpty
                  onChange={(e) => updateCondition(index, 'type', e.target.value)}
                >
                  {CONDITION_TYPES.map((ct) => (
                    <MenuItem key={ct.value} value={ct.value}>
                      {ct.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                size="small"
                options={assessmentOptions}
                value={selectedAssessment}
                onChange={(_, newVal) => updateCondition(index, 'instrument_code', newVal?.code || '')}
                getOptionLabel={(o) => o?.label || o?.code || ''}
                isOptionEqualToValue={(o, v) => o?.code === v?.code}
                renderInput={(params) => (
                  <TextField {...params} label="Instrument" />
                )}
                sx={{ minWidth: 240 }}
              />
              {condition.type === 'assessment_trend' && (
                <TextField
                  size="small"
                  type="number"
                  value={condition.count || ''}
                  onChange={(e) => updateCondition(index, 'count', parseInt(e.target.value) || '')}
                  placeholder="N"
                  sx={{ width: 60 }}
                />
              )}
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={condition.operator || ''}
                  displayEmpty
                  onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                >
                  {(conditionType?.operators || ['equals']).map((op) => (
                    <MenuItem key={op} value={op}>
                      {op.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!['increasing', 'decreasing'].includes(condition.operator) && (
                <TextField
                  size="small"
                  type="number"
                  label={range ? `Value (${range.min}–${range.max})` : 'Value'}
                  value={condition.value ?? ''}
                  onChange={(e) => updateCondition(index, 'value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                  inputProps={range ? { min: range.min, max: range.max } : undefined}
                  error={outOfRange}
                  sx={{ width: 140 }}
                />
              )}
              {outOfRange && range && (
                <Tooltip title={`Value must be between ${range.min} and ${range.max} for ${range.name || condition.instrument_code}.`}>
                  <Chip
                    size="small"
                    color="warning"
                    icon={<WarningIcon sx={{ fontSize: 14 }} />}
                    label="out of range"
                    sx={{ height: 22, fontSize: '0.6875rem' }}
                  />
                </Tooltip>
              )}
              <IconButton onClick={() => removeCondition(index)} size="small" sx={{ ml: 'auto', flexShrink: 0 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )
        })}

        {formData.rule_definition.conditions.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Add at least one condition for this rule to work.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6875rem' }}>
            Actions (What to Do)
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addAction}>
            Add Action
          </Button>
        </Box>

        {formData.rule_definition.actions.map((action, index) => {
          const isRiskAction = action.type === 'set_risk_factor' || action.type === 'clear_risk_factor'
          const isOrphan = isRiskAction && action.risk_code && !interventionCodeSet.has(action.risk_code)
          return (
            <Box key={index} display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1, px: 1, py: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 36, fontWeight: 500, flexShrink: 0 }}>
                {index === 0 ? 'Then' : 'AND'}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={action.type}
                  displayEmpty
                  onChange={(e) => updateAction(index, { type: e.target.value, risk_code: '', value: '' })}
                >
                  {ACTION_TYPES.map((at) => (
                    <MenuItem key={at.value} value={at.value}>
                      {at.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isRiskAction && renderRiskCodeAutocomplete(action, index)}
              {action.type === 'set_risk_factor' && renderSetRiskValueControl(action, index)}
              {action.type === 'set_risk_factor' && (
                <TextField
                  size="small"
                  type="number"
                  value={action.expires_in_days || ''}
                  onChange={(e) => updateAction(index, { expires_in_days: parseInt(e.target.value) || null })}
                  placeholder="Expires (days)"
                  sx={{ width: 130 }}
                />
              )}
              {isOrphan && (
                <Tooltip title="This risk code is not attached to this intervention. Re-add it in the Risk Codes tab, or change this rule to use a different code.">
                  <Chip
                    size="small"
                    color="warning"
                    icon={<WarningIcon sx={{ fontSize: 14 }} />}
                    label="not in intervention"
                    sx={{ height: 22, fontSize: '0.6875rem' }}
                  />
                </Tooltip>
              )}
              <IconButton onClick={() => removeAction(index)} size="small" sx={{ ml: 'auto', flexShrink: 0 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )
        })}

        {formData.rule_definition.actions.length === 0 && (
          <Alert severity="warning">
            Add at least one action for this rule to have an effect.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Rule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ContextRuleEditorDialog
